'use client';

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Modal } from '@swordhealth/ui-corporate';
import DynamicBody from '@/components/core/DynamicBody';
import { AppSupport, Footer } from '@/app/_components/platform';
import { FaqBanner, Faqs } from '@/app/components/shared';

import { ClientProvider } from '@/contexts/client.context';
import { products } from '@/utils/products';
import HeroWithCards from '@/components/platform/HeroWithCards';
import ClientSelect from '../eligibility-check/ClientSelect/ClientSelect';

const MainPage = ({ content }) => {
  const {
    body,
    app,
    bottomCTA,
    disclaimer,
    footnotes,
    faqs,
    contact,
    faqBanner,
    heroRolling,
    solutionConditions,
    exitPopup,
  } = content;
  const searchParams = useSearchParams();
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [clientKey, setClientKey] = useState(null);
  const inactivityTimer = useRef(null);
  const hasScrolled = useRef(false);
  const EXIT_POPUP_KEY = 'exitPopupShown';

  const showPopup = () => {
    if (!localStorage.getItem(EXIT_POPUP_KEY) && exitPopup) {
      setModalIsOpen(true);
      setShowExitPopup(true);
      localStorage.setItem(EXIT_POPUP_KEY, 'true');
    }
  };

  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        showPopup();
      }
    };

    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const triggerY = window.innerHeight * 0.35;
      hasScrolled.current = scrollY > triggerY;
    };

    const checkAndShowPopup = () => {
      if (hasScrolled.current) {
        showPopup();
      }
    };

    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(checkAndShowPopup, 15000);
    };

    const activityEvents = ['keydown', 'mousedown', 'touchstart', 'mousemove'];

    window.addEventListener('mouseout', handleMouseLeave);
    window.addEventListener('scroll', handleScroll);
    activityEvents.forEach((event) => window.addEventListener(event, resetInactivityTimer));

    resetInactivityTimer();

    return () => {
      window.removeEventListener('mouseout', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
      activityEvents.forEach((event) => window.removeEventListener(event, resetInactivityTimer));
      clearTimeout(inactivityTimer.current);
    };
  }, []);

  const handleClosePopup = () => {
    setModalIsOpen(false);
    setShowExitPopup(false);
    document.documentElement.classList.remove('ui-html-no-scroll');
  };

  const contentPopUp = {
    searchTitles: [],
    search_description_title:
      'You came here for relief. <br><strong>Let’s get you there.</strong> <br>',
    search_description:
      'Provide the name of the employer, health plan, or organization that offers you Sword as a benefit.',
    search_placeholder: "E.g.Domino's, Blue Health",
    search_cta: {
      button: {
        label: 'Check my coverage',
      },
    },
  };

  return (
    <>
      <ClientProvider
        defaultClient={{
          clientKey: null,
          onboardingUrl: '',
          institutionKey: null,
          launchDate: null,
          isActive: true,
          product: products.PLATFORM,
        }}
      >
        {showExitPopup && exitPopup && !clientKey && (
          <Modal id="exit-popup" open={modalIsOpen} showCloseButton={false} centeredContent>
            <ClientSelect
              content={contentPopUp}
              solution="platform"
              setClientKey={setClientKey}
              params={searchParams}
              onClose={handleClosePopup}
              isExitPopup
              skipProgram
            />
          </Modal>
        )}
        {heroRolling && (
          <HeroWithCards
            content={heroRolling}
            solutionConditions={solutionConditions}
            params={searchParams}
          />
        )}
        <DynamicBody body={body} isOrganicLP />
        {faqs && <Faqs content={faqs} />}
        <FaqBanner contact={contact} content={faqBanner} />
        {app && <AppSupport content={app} />}
        <Footer
          bottomCTA={bottomCTA}
          disclaimer={disclaimer}
          footnotes={footnotes}
          email={contact?.email}
          isOrganicLP
        />
      </ClientProvider>
    </>
  );
};

export default MainPage;
